import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { TooltipComponent } from '@syncfusion/ej2-react-popups'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import type { PatientFlowOverviewResult } from '@services/api/dashboard/scheduling.api'
import { isDashboardDevMocksEnabled } from '@utils/dashboardDevMocks.util'
import {
  hasNonZeroDashboardValues,
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import { getPatientFlowData } from '../dashboardOverview.mockData'

const PATIENT_FLOW_OVERVIEW_NAME_PREVIEW = 5
const PATIENT_FLOW_OVERVIEW_TOOLTIP_NAME_LIMIT = 10

const escapeHtmlForTooltip = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

const buildPatientFlowOverflowTooltipHtml = (
  overflowNames: string[],
): string => {
  const visible = overflowNames.slice(
    0,
    PATIENT_FLOW_OVERVIEW_TOOLTIP_NAME_LIMIT,
  )
  const remaining = overflowNames.length - visible.length
  const lines = visible.map(escapeHtmlForTooltip)
  if (remaining > 0) {
    lines.push(`and ${remaining} more`)
  }
  return lines.join('<br/>')
}

type PatientFlowTrackerCardProps = {
  patientFlowOverview: PatientFlowOverviewResult | null
  loading: boolean
}

export function PatientFlowTrackerCard({
  patientFlowOverview,
  loading,
}: PatientFlowTrackerCardProps): React.ReactElement {
  const useMocks = isDashboardDevMocksEnabled()
  const columns = useMemo(() => {
    if (patientFlowOverview?.columns?.length) {
      return patientFlowOverview.columns.map((col) => ({
        header: col.header,
        count: String(col.count),
        names: col.names,
      }))
    }
    return useMocks ? getPatientFlowData('1D') : []
  }, [patientFlowOverview, useMocks])
  const isEmpty = shouldShowDashboardSectionEmpty(
    loading,
    columns.some(
      (col) => hasNonZeroDashboardValues(col.count) || col.names.length > 0,
    ),
  )
  const pending = isDashboardSectionPending(
    loading,
    patientFlowOverview != null,
  )

  return (
    <DashboardSectionCard
      title="Patient Flow Tracker"
      titleHref="/dashboard/patient-flow"
      className="dashboard-patient-flow-card"
      loading={pending}
      skeleton={{ block: 'tall' }}
      isEmpty={isEmpty}
    >
      <div className="dashboard-patient-flow-content">
        {columns.map((col) => {
          const previewNames = col.names.slice(
            0,
            PATIENT_FLOW_OVERVIEW_NAME_PREVIEW,
          )
          const overflowNames = col.names.slice(
            PATIENT_FLOW_OVERVIEW_NAME_PREVIEW,
          )
          const moreCount = overflowNames.length
          return (
            <div key={col.header} className="dashboard-patient-flow-column">
              <div className="dashboard-patient-flow-header">{col.header}</div>
              <div className="dashboard-patient-flow-count">{col.count}</div>
              <ul className="dashboard-patient-flow-list">
                {previewNames.map((name, index) => (
                  <li key={`${col.header}-${index}-${name}`}>{name}</li>
                ))}
                {moreCount > 0 && (
                  <li className="dashboard-patient-flow-more">
                    <TooltipComponent
                      content={buildPatientFlowOverflowTooltipHtml(
                        overflowNames,
                      )}
                      opensOn="Hover"
                      enableHtmlParse
                      cssClass="e-tooltip-template-css e-tooltip-tab-feature dashboard-patient-flow-more-tooltip"
                      position="TopCenter"
                    >
                      <Link
                        to="/dashboard/patient-flow"
                        className="dashboard-patient-flow-more-link"
                      >
                        +{moreCount} more
                      </Link>
                    </TooltipComponent>
                  </li>
                )}
              </ul>
            </div>
          )
        })}
      </div>
    </DashboardSectionCard>
  )
}
